import { useState } from "react";
import StepNav, { NUTRITION_STEPS } from "@/components/StepNav";
import { NUTRITION_CASES, serveCase, solveCase } from "@/data/nutritionCases";
import { useCaseList } from "@/context/ContentContext";
import { IntroPanel } from "@/components/nutrition/NutritionParts";
import {
  AssessBeat,
  CalcBeat,
  DietBeat,
  PlanBeat,
  EducateBeat,
} from "@/components/nutrition/NutritionBeats";
import { NutritionDebrief } from "@/components/nutrition/NutritionDebrief";
import { NutritionQuiz } from "@/components/nutrition/NutritionQuiz";

const pickRandom = (cases, excludeId) => {
  const pool = cases.filter((c) => c.id !== excludeId);
  const list = pool.length ? pool : cases;
  return list[Math.floor(Math.random() * list.length)];
};

export default function Nutrition() {
  const cases = useCaseList("nutrition", NUTRITION_CASES);
  const [stage, setStage] = useState("intro"); // intro | running | debrief
  const [caseData, setCaseData] = useState(null);
  const [solved, setSolved] = useState(null);
  const [beatIdx, setBeatIdx] = useState(0);
  const [scores, setScores] = useState([]);
  const [debrief, setDebrief] = useState(null);

  const beginCase = (excludeId) => {
    const c = serveCase(pickRandom(cases, excludeId));
    setCaseData(c);
    setSolved(solveCase(c));
    setBeatIdx(0);
    setScores([]);
    setDebrief(null);
    setStage("running");
  };

  const handleBeatDone = (scoreObj) => {
    const next = [...scores, scoreObj];
    if (beatIdx < 4) {
      setScores(next);
      setBeatIdx(beatIdx + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Final beat — compute the debrief.
    const earned = next.reduce((a, b) => a + b.earned, 0);
    const max = next.reduce((a, b) => a + b.max, 0) || 1;
    setDebrief({
      percent: Math.round((earned / max) * 100),
      breakdown: next,
      solved,
      caseTitle: caseData.title,
      c: caseData,
    });
    setStage("debrief");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentStep = stage === "running" ? beatIdx + 1 : stage === "debrief" ? 5 : 1;

  const beatProps = { c: caseData, solved, onDone: handleBeatDone };
  const beats = [
    <AssessBeat key="assess" {...beatProps} />,
    <CalcBeat key="calc" {...beatProps} />,
    <DietBeat key="diet" {...beatProps} />,
    <PlanBeat key="plan" {...beatProps} />,
    <EducateBeat key="educate" {...beatProps} />,
  ];

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={currentStep}
        steps={NUTRITION_STEPS}
        title="Nutrition"
        subtitle="Cert IV nutrition calculator drills"
      />

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === "intro" && (
          <IntroPanel
            caseCount={cases.length}
            onStartCase={() => beginCase(null)}
            onStartQuiz={() => setStage("quiz")}
          />
        )}

        {stage === "quiz" && <NutritionQuiz onExit={() => setStage("intro")} />}

        {stage === "running" && caseData && (
          <div data-testid="nutrition-running">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#0047AB] font-semibold">
                  Case · {caseData.name}
                </div>
                <div className="text-sm text-slate-600">
                  {caseData.species === "cat" ? "Feline" : "Canine"} · {caseData.breed} · {caseData.signalment}
                </div>
              </div>
              <div className="flex gap-1.5">
                {NUTRITION_STEPS.map((s) => (
                  <span
                    key={s.id}
                    className={`w-6 h-1.5 rounded-full ${s.id <= currentStep ? "bg-[#0047AB]" : "bg-slate-200"}`}
                  />
                ))}
              </div>
            </div>
            {beats[beatIdx]}
          </div>
        )}

        {stage === "debrief" && debrief && (
          <NutritionDebrief
            result={debrief}
            onNext={() => beginCase(caseData.id)}
            onRestart={() => setStage("intro")}
          />
        )}
      </main>
    </div>
  );
}
